import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  Languages,
  ListTodo,
  Loader2,
  Lock,
  Mic,
  NotebookPen,
  Plus,
  Scale,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import EarlyBirdModal from '@/components/EarlyBirdModal';
import CollaborationStrip from '@/components/CollaborationStrip';
import FeatureHistoryMenu from '@/components/FeatureHistoryMenu';
import MatterWorkspaceContext, { withQueryParams } from '@/components/matter/MatterWorkspaceContext';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { reviewMeta as collaborationReviewMeta } from '@/lib/collaboration';
import { recordJourneyVisit } from '@/lib/chatJourney';

const PREMIUM_PLANS = new Set(['PRO', 'TEAM', 'FIRM', 'EARLY_BIRD']);

function hasJollyAccess(user) {
  if (!user) return false;
  const enabledFeatures = new Set((user.enabled_features || []).map(f => String(f).toLowerCase()));
  const featureFlags = user.feature_flags || {};
  return (
    PREMIUM_PLANS.has(String(user.plan || 'FREE').toUpperCase())
    || Boolean(user.jolly_early_access)
    || enabledFeatures.has('jolly')
    || enabledFeatures.has('war_room')
    || Boolean(featureFlags.jolly)
    || Boolean(featureFlags.war_room)
  );
}

const MODE_STORAGE_KEY = 'advoq-jolly-mode';
const DEFAULT_MODE = 'dictation';
const CHUNK_TIMESLICE_MS = 4000;
const MIN_RELIABLE_CHUNK_BYTES = 4096;
const MIN_RELIABLE_CHUNK_MS = 350;
const RECORDER_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

const JOLLY_MODES = [
  {
    value: 'client_call',
    label: 'Conversation With Client',
    shortLabel: 'Client',
    kicker: 'Client',
    description: 'Capture facts, disputes, risks, and follow-ups from a client conversation.',
    accent: '#f59e0b',
  },
  {
    value: 'brief_me',
    label: 'Case Mock',
    shortLabel: 'Case mock',
    kicker: 'Briefing',
    description: 'Turn a spoken case walkthrough into hearing notes, issues, and next questions.',
    accent: '#22c55e',
  },
  {
    value: 'dictation',
    label: 'Personal Thoughts',
    shortLabel: 'Thoughts',
    kicker: 'Private note',
    description: 'Save your own thinking, chronology, and next moves without cleaning it up first.',
    accent: '#38bdf8',
  },
  {
    value: 'post_hearing_debrief',
    label: 'Post-Hearing Debrief',
    shortLabel: 'Debrief',
    kicker: 'After court',
    description: 'Capture what happened, what the bench cared about, and what follows next.',
    accent: '#c084fc',
  },
];

const LANGUAGE_OPTIONS = [
  { value: 'unknown', label: 'Auto-detect (Hindi + English)' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'en-IN', label: 'English' },
  { value: 'bn-IN', label: 'Bengali' },
  { value: 'gu-IN', label: 'Gujarati' },
  { value: 'kn-IN', label: 'Kannada' },
  { value: 'ml-IN', label: 'Malayalam' },
  { value: 'mr-IN', label: 'Marathi' },
  { value: 'or-IN', label: 'Odia' },
  { value: 'pa-IN', label: 'Punjabi' },
  { value: 'ta-IN', label: 'Tamil' },
  { value: 'te-IN', label: 'Telugu' },
];

const LANGUAGE_LABELS = LANGUAGE_OPTIONS.reduce((acc, option) => {
  const base = String(option.value || '').split('-', 1)[0];
  if (base) acc[base] = option.label;
  return acc;
}, { en: 'English', hi: 'Hindi', bn: 'Bengali', gu: 'Gujarati', kn: 'Kannada', ml: 'Malayalam', mr: 'Marathi', or: 'Odia', pa: 'Punjabi', ta: 'Tamil', te: 'Telugu', unknown: 'Auto-detect' });

const OUTPUT_SECTIONS = [
  { key: 'facts', label: 'Facts', types: ['fact', 'admitted_fact', 'disputed_fact', 'party_role', 'authority_reference'], icon: NotebookPen },
  { key: 'issues', label: 'Issues', types: ['issue', 'risk_flag'], icon: Scale },
  { key: 'actions', label: 'Action Items', types: ['action_item', 'deadline', 'draft_prompt', 'matter_update'], icon: ListTodo },
  { key: 'chronology', label: 'Chronology', types: ['chronology_candidate'], icon: CalendarDays },
  { key: 'hearing', label: 'Hearing Notes', types: ['hearing_note'], icon: Mic },
  { key: 'research', label: 'Research Follow-ups', types: ['research_question'], icon: Bot },
];

const reviewOrder = {
  accepted: 0,
  needs_review: 1,
  reviewed: 2,
  discarded: 3,
};

function getModeMeta(mode) {
  return JOLLY_MODES.find(item => item.value === mode) || JOLLY_MODES.find(item => item.value === DEFAULT_MODE) || JOLLY_MODES[0];
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds || 0)));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function formatTimeLabel(ms) {
  if (ms == null) return '';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function formatSessionTimestamp(value) {
  if (!value) return 'Just now';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Just now';
  }
}

function normalizeReviewStatus(status) {
  const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'accepted' || normalized === 'discarded' || normalized === 'reviewed') return normalized;
  return 'needs_review';
}

function formatDetectedLanguage(code) {
  const normalized = String(code || '').trim().toLowerCase();
  if (!normalized) return 'Unknown';
  const base = normalized.split('-', 1)[0];
  return LANGUAGE_LABELS[base] || code;
}

function getRetentionCounts(session) {
  if (!session) return { accepted: 0, discarded: 0, pending: 0, visibleToAgents: 0 };

  const outputs = session.structured_outputs || [];
  if (outputs.length) {
    let accepted = 0;
    let discarded = 0;
    let pending = 0;
    outputs.forEach(output => {
      const status = normalizeReviewStatus(output.review_status);
      if (status === 'accepted') accepted += 1;
      else if (status === 'discarded') discarded += 1;
      else pending += 1;
    });
    return {
      accepted,
      discarded,
      pending,
      visibleToAgents: session?.transcript_reviewed_by_user ? accepted : 0,
    };
  }

  const accepted = Number(session.retained_output_count || 0);
  const discarded = Number(session.discarded_output_count || 0);
  const pending = Number(session.pending_output_count || 0);
  return {
    accepted,
    discarded,
    pending,
    visibleToAgents: session?.transcript_reviewed_by_user ? accepted : 0,
  };
}

function getRecorderMimeType() {
  return RECORDER_MIME_TYPES.find(type => window.MediaRecorder?.isTypeSupported?.(type)) || '';
}

function extensionForMimeType(mimeType) {
  const normalized = String(mimeType || '').toLowerCase().split(';', 1)[0].trim();
  if (normalized === 'audio/mp4') return '.mp4';
  if (normalized === 'audio/mpeg' || normalized === 'audio/mp3') return '.mp3';
  if (normalized === 'audio/wav' || normalized === 'audio/x-wav') return '.wav';
  if (normalized === 'audio/ogg') return '.ogg';
  if (normalized === 'audio/opus') return '.opus';
  return '.webm';
}

function statusMeta(status) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'ready') {
    return { label: 'Ready', tone: 'rgba(34,197,94,0.14)', color: '#22c55e', icon: CheckCircle2 };
  }
  if (normalized === 'failed') {
    return { label: 'Needs attention', tone: 'rgba(239,68,68,0.14)', color: '#ef4444', icon: XCircle };
  }
  if (normalized === 'recording') {
    return { label: 'Recording', tone: 'rgba(248,113,113,0.15)', color: '#f87171', icon: Mic };
  }
  if (normalized === 'processing') {
    return { label: 'Structuring', tone: 'rgba(59,130,246,0.14)', color: '#60a5fa', icon: Loader2 };
  }
  return { label: 'Draft', tone: 'rgba(148,163,184,0.14)', color: 'var(--text-muted)', icon: CircleDashed };
}

function confidenceMeta(output) {
  const label = (output?.confidence_label || '').toLowerCase();
  const score = Number(output?.confidence || 0);
  if (label === 'certain' || score >= 0.82) {
    return { label: 'Certain', color: '#22c55e', bg: 'rgba(34,197,94,0.14)' };
  }
  if (label === 'possible' || label === 'inferred' || score < 0.68) {
    return { label: label === 'inferred' ? 'Inferred' : 'Possible', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)' };
  }
  return { label: 'Needs review', color: '#60a5fa', bg: 'rgba(59,130,246,0.14)' };
}

function reviewTone(status) {
  return collaborationReviewMeta(status);
}

function classificationState(session) {
  return String(session?.classification_status || 'confirmed').toLowerCase() === 'pending' ? 'pending' : 'confirmed';
}

function SessionListItem({ session, isActive, onClick, onDelete }) {
  const meta = statusMeta(session.status);
  const modeMeta = getModeMeta(session.mode);
  const Icon = meta.icon;
  const retention = getRetentionCounts(session);
  const pendingClassification = classificationState(session) === 'pending';

  return (
    <div
      style={{
        width: '100%',
        padding: '14px 15px',
        borderRadius: '14px',
        border: `1px solid ${isActive ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
        background: isActive ? 'var(--accent-glow)' : 'var(--bg-surface)',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '10px',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        style={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          cursor: 'pointer',
          minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                background: 'var(--bg-elevated)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: modeMeta.accent,
              }}>
                <Mic size={14} />
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                {pendingClassification ? 'Classify later' : modeMeta.shortLabel}
              </span>
            </div>
            <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.4 }}>
              {session.title || 'Untitled session'}
            </p>
          </div>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '4px 8px',
            borderRadius: '999px',
            background: meta.tone,
            color: meta.color,
            fontSize: '10px',
            fontWeight: 700,
            flexShrink: 0,
          }}>
            <Icon size={10} className={meta.icon === Loader2 ? 'animate-spin' : ''} />
            {meta.label}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {session.matter_name || session.matter_id}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {session.duration_seconds ? formatDuration(session.duration_seconds) : 'Pending'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {formatSessionTimestamp(session.updated_at)}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={smallChipStyle}>
              {retention.accepted} kept
            </span>
            <span style={smallChipStyle}>
              {retention.pending} pending
            </span>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={onDelete}
        className="app-chat-icon-button h-9 w-9 rounded-xl"
        title="Move session to trash"
        style={{ alignSelf: 'flex-start' }}
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function ModeChoiceCard({ item, active, onClick, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: compact ? '12px 13px' : '14px',
        borderRadius: '14px',
        border: `1px solid ${active ? item.accent : 'var(--border-subtle)'}`,
        background: active ? `${item.accent}14` : 'var(--bg-surface)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '4px' : '6px',
      }}
    >
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontWeight: 700 }}>
        {item.kicker}
      </div>
      <div style={{ color: 'var(--text-primary)', fontSize: compact ? '13px' : '14px', fontWeight: 700 }}>
        {item.label}
      </div>
      {!compact ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
          {item.description}
        </div>
      ) : null}
    </button>
  );
}

function TranscriptSegment({ segment }) {
  const hasOriginal = segment.raw_text && segment.normalized_text &&
    segment.raw_text.trim() !== segment.normalized_text.trim();

  return (
    <div
      id={`segment-${segment.segment_id}`}
      style={{
        padding: '12px 14px',
        borderRadius: '12px',
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
            Segment {segment.sequence_index}
          </span>
          {segment.speaker_label ? (
            <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '999px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
              {segment.speaker_label}
            </span>
          ) : null}
        </div>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          {formatTimeLabel(segment.start_ms)}
          {segment.end_ms != null ? ` -> ${formatTimeLabel(segment.end_ms)}` : ''}
        </span>
      </div>
      <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.7 }}>
        {segment.normalized_text || segment.raw_text}
      </p>
      {hasOriginal ? (
        <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.65, borderTop: '1px solid var(--border-subtle)', paddingTop: '6px' }}>
          <span style={{ fontWeight: 700, marginRight: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '10px' }}>Original:</span>
          {segment.raw_text}
        </p>
      ) : null}
    </div>
  );
}

function OutputCard({ output, matterId, onReview, onOpenResearch, onJumpToCitation }) {
  const confidence = confidenceMeta(output);
  const review = reviewTone(output.collaboration_review_state || output.review_status);
  const outputType = String(output.output_type || '').replace(/_/g, ' ');

  return (
    <div style={{
      borderRadius: '14px',
      border: '1px solid var(--border-subtle)',
      background: 'var(--bg-surface)',
      padding: '14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <span style={typeChipStyle}>
              {outputType}
            </span>
            <span style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: confidence.bg, color: confidence.color, fontWeight: 700 }}>
              {confidence.label}
            </span>
            <span style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: review.bg, color: review.color, fontWeight: 700 }}>
              {review.label}
            </span>
          </div>
          <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.7, fontWeight: 600 }}>
            {output.content}
          </p>
        </div>
      </div>

      {output.citations?.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {output.citations.slice(0, 4).map(citation => (
            <button
              key={citation.citation_id}
              type="button"
              onClick={() => onJumpToCitation?.(citation)}
              style={citationButtonStyle}
            >
              {(citation.offsets?.start_ms != null || citation.offsets?.end_ms != null)
                ? `${formatTimeLabel(citation.offsets?.start_ms)}`
                : 'Transcript'}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <CollaborationStrip
          matterId={matterId}
          targetType="jolly_output"
          targetId={output.structured_output_id}
          targetLabel={output.content}
          initialState={output.collaboration || { review_state: output.collaboration_review_state || output.review_status }}
          fallbackReviewState={output.collaboration_review_state || output.review_status || 'new'}
          onReviewChange={(nextState) => {
            const payload = nextState === 'accepted'
              ? { review_status: 'accepted', accepted_destination: output.output_type }
              : nextState === 'rejected'
                ? { review_status: 'discarded' }
                : nextState === 'needs_follow_up'
                  ? { review_status: 'reviewed' }
                  : nextState === 'reviewed'
                    ? { review_status: 'reviewed' }
                    : { review_status: 'needs_review' };
            return onReview(output.structured_output_id, payload);
          }}
          compact
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {output.output_type === 'research_question' ? (
          <button
            type="button"
            onClick={() => onOpenResearch?.(output.content)}
            style={{ ...outputActionStyle, background: 'rgba(59,130,246,0.14)', color: '#60a5fa', borderColor: 'rgba(59,130,246,0.2)' }}
          >
            Open in Research
          </button>
        ) : null}
        </div>
      </div>
    </div>
  );
}

export default function JollyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();

  const [viewMode, setViewMode] = useState('home');
  const [showSetup, setShowSetup] = useState(false);
  const [matters, setMatters] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedMatterId, setSelectedMatterId] = useState('');
  const [selectedMatterName, setSelectedMatterName] = useState('');
  const [newMatterName, setNewMatterName] = useState('');
  const [historyQuery, setHistoryQuery] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [currentSession, setCurrentSession] = useState(null);
  const [mode, setMode] = useState(() => window.localStorage.getItem(MODE_STORAGE_KEY) || DEFAULT_MODE);
  const [classificationTouched, setClassificationTouched] = useState(false);
  const [languageHint, setLanguageHint] = useState('unknown');
  const [sessionTitle, setSessionTitle] = useState('');
  const [editedTranscript, setEditedTranscript] = useState('');
  const [transcriptDirty, setTranscriptDirty] = useState(false);
  const [showOriginalTranscript, setShowOriginalTranscript] = useState(false);
  // Transcript view: 'original' | 'english' | 'hindi'
  const [transcriptView, setTranscriptView] = useState('original');
  const [translating, setTranslating] = useState(false);
  const [editedEnglishTranscript, setEditedEnglishTranscript] = useState('');
  const [editedHindiTranscript, setEditedHindiTranscript] = useState('');
  const [translatedDirty, setTranslatedDirty] = useState(false);
  const [analysisDialog, setAnalysisDialog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSession, setLoadingSession] = useState(false);
  const [creatingMatter, setCreatingMatter] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [savingSessionMeta, setSavingSessionMeta] = useState(false);
  const [recording, setRecording] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showEarlyBird, setShowEarlyBird] = useState(false);
  const [showMatterSelector, setShowMatterSelector] = useState(false);

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const uploadQueueRef = useRef(Promise.resolve());
  const chunkSequenceRef = useRef(0);
  const nextChunkStartMsRef = useRef(0);
  const recordingStartedAtRef = useRef(0);
  const timerRef = useRef(null);
  const selectedSessionIdRef = useRef('');
  const currentSessionRef = useRef(null);
  const recordingRef = useRef(false);
  const initChunkRef = useRef(null);

  const selectedMatter = useMemo(
    () => matters.find(item => item.matter_id === selectedMatterId) || null,
    [matters, selectedMatterId],
  );

  const currentMode = useMemo(() => getModeMeta(mode), [mode]);
  const currentStatus = useMemo(() => statusMeta(currentSession?.status), [currentSession?.status]);
  const retention = useMemo(() => getRetentionCounts(currentSession), [currentSession]);
  const sessionClassification = classificationState(currentSession);
  const sessionModeMeta = useMemo(() => getModeMeta(currentSession?.mode || mode), [currentSession?.mode, mode]);

  const groupedOutputs = useMemo(() => {
    const outputs = (currentSession?.structured_outputs || [])
      .filter(output => output.output_type !== 'summary')
      .slice()
      .sort((left, right) => (reviewOrder[normalizeReviewStatus(left.review_status)] || 0) - (reviewOrder[normalizeReviewStatus(right.review_status)] || 0));

    return OUTPUT_SECTIONS.map(section => ({
      ...section,
      items: outputs.filter(output => section.types.includes(output.output_type)),
    })).filter(section => section.items.length > 0);
  }, [currentSession]);

  const sessionSummary = useMemo(() => {
    if (!currentSession) return '';
    if (currentSession.summary) return currentSession.summary;
    const summaryOutput = (currentSession.structured_outputs || []).find(output => output.output_type === 'summary');
    return summaryOutput?.content || '';
  }, [currentSession]);

  const transcriptValue = currentSession?.reviewed_transcript || currentSession?.final_transcript || currentSession?.live_transcript || '';
  const sessionTitleDirty = Boolean(currentSession) && (sessionTitle.trim() || currentSession?.title || '') !== (currentSession?.title || '');
  const modeDirty = Boolean(currentSession) && mode !== (currentSession?.mode || DEFAULT_MODE);
  const showRecordingSurface = recording || currentSession?.status === 'recording';

  const upsertSessionSummary = useCallback((sessionDoc) => {
    if (!sessionDoc?.session_id) return;
    setSessions(prev => [sessionDoc, ...prev.filter(item => item.session_id !== sessionDoc.session_id)]);
  }, []);

  const stopMediaStream = useCallback((stream = mediaStreamRef.current) => {
    stream?.getTracks?.().forEach(track => track.stop());
    if (!stream || mediaStreamRef.current === stream) {
      mediaStreamRef.current = null;
    }
  }, []);

  const clearRecorderState = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    recordingRef.current = false;
    mediaRecorderRef.current = null;
    stopMediaStream();
  }, [stopMediaStream]);

  const syncCurrentSession = useCallback((sessionDoc, { preserveTranscript = false } = {}) => {
    if (!sessionDoc) return;
    selectedSessionIdRef.current = sessionDoc.session_id || '';
    currentSessionRef.current = sessionDoc;
    setCurrentSession(sessionDoc);
    if (sessionDoc.session_id) {
      setSelectedSessionId(sessionDoc.session_id);
      upsertSessionSummary(sessionDoc);
    }
    if (sessionDoc.title != null) setSessionTitle(sessionDoc.title || '');
    if (sessionDoc.mode) setMode(sessionDoc.mode);
    setClassificationTouched(classificationState(sessionDoc) === 'confirmed');
    if (!preserveTranscript) {
      setEditedTranscript(sessionDoc.reviewed_transcript || sessionDoc.final_transcript || sessionDoc.live_transcript || '');
      setTranscriptDirty(false);
      setEditedEnglishTranscript(sessionDoc.english_transcript || '');
      setEditedHindiTranscript(sessionDoc.hindi_transcript || '');
      setTranslatedDirty(false);
    }
  }, [upsertSessionSummary]);

  const navigateToHome = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedMatterId) params.set('matter_id', selectedMatterId);
    if (selectedMatterName) params.set('matter_name', selectedMatterName);
    const next = params.toString();
    navigate(next ? `/jolly?${next}` : '/jolly');
  }, [navigate, selectedMatterId, selectedMatterName]);

  const openHomeView = useCallback(() => {
    setViewMode('home');
    navigateToHome();
  }, [navigateToHome]);

  const openSessionView = useCallback((sessionDoc, { fromHistory = false } = {}) => {
    const sessionId = typeof sessionDoc === 'string' ? sessionDoc : sessionDoc?.session_id;
    if (!sessionId) return;

    const matterId = typeof sessionDoc === 'string' ? (currentSessionRef.current?.matter_id || selectedMatterId) : (sessionDoc.matter_id || selectedMatterId);
    const matterName = typeof sessionDoc === 'string' ? (currentSessionRef.current?.matter_name || selectedMatterName) : (sessionDoc.matter_name || selectedMatterName);

    selectedSessionIdRef.current = sessionId;
    setSelectedSessionId(sessionId);
    setViewMode(fromHistory ? 'history' : 'session');

    const params = new URLSearchParams();
    params.set('session_id', sessionId);
    if (matterId) params.set('matter_id', matterId);
    if (matterName) params.set('matter_name', matterName);
    navigate(`/jolly?${params.toString()}`);
  }, [navigate, selectedMatterId, selectedMatterName]);

  const openHistoryView = useCallback(() => {
    const fallback = selectedSessionIdRef.current || sessions[0]?.session_id || '';
    setViewMode('history');
    if (fallback) {
      const sessionDoc = sessions.find(item => item.session_id === fallback);
      openSessionView(sessionDoc || fallback, { fromHistory: true });
    } else {
      navigateToHome();
    }
  }, [navigateToHome, openSessionView, sessions]);

  const loadMatters = useCallback(async () => {
    try {
      const response = await fetch(`${API}/matters`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!response.ok) throw new Error('Failed to load matters');
      const data = await response.json();
      setMatters(data);
    } catch {
      toast.error('Failed to load matters');
    }
  }, [getAuthHeaders]);

  const loadSessions = useCallback(async (matterOverride) => {
    try {
      const params = new URLSearchParams();
      const matterId = matterOverride ?? selectedMatterId;
      if (matterId) params.set('matter_id', matterId);
      if (historyQuery.trim()) params.set('q', historyQuery.trim());
      const response = await fetch(`${API}/jolly/sessions?${params.toString()}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!response.ok) throw new Error('Failed to load sessions');
      const data = await response.json();
      setSessions(data);
    } catch {
      toast.error('Failed to load Jolly sessions');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, historyQuery, selectedMatterId]);

  const loadSessionDetail = useCallback(async (sessionId, { silent = false } = {}) => {
    if (!sessionId) return;
    if (!silent) setLoadingSession(true);
    try {
      const response = await fetch(`${API}/jolly/sessions/${sessionId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!response.ok) throw new Error('Failed to load session detail');
      const data = await response.json();
      const changingSession = currentSessionRef.current?.session_id !== sessionId;
      syncCurrentSession(data, { preserveTranscript: !changingSession && transcriptDirty });
      if (data.matter_id) {
        setSelectedMatterId(data.matter_id);
        setSelectedMatterName(data.matter_name || data.matter_id);
      }
    } catch {
      if (!silent) toast.error('Failed to load session detail');
    } finally {
      if (!silent) setLoadingSession(false);
    }
  }, [getAuthHeaders, syncCurrentSession, transcriptDirty]);

  const createMatter = async () => {
    if (!newMatterName.trim()) return;
    setCreatingMatter(true);
    try {
      const response = await fetch(`${API}/matters`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ name: newMatterName.trim() }),
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!response.ok) throw new Error('Failed to create matter');
      const matter = await response.json();
      setMatters(prev => [matter, ...prev.filter(item => item.matter_id !== matter.matter_id)]);
      setSelectedMatterId(matter.matter_id);
      setSelectedMatterName(matter.name);
      setNewMatterName('');
      // If we're in session view with no linked matter, auto-link
      if (currentSessionRef.current?.session_id && !currentSessionRef.current?.matter_id) {
        const linkResp = await fetch(`${API}/jolly/sessions/${currentSessionRef.current.session_id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({ matter_id: matter.matter_id }),
        });
        if (linkResp.ok) {
          const updated = await linkResp.json();
          syncCurrentSession({ ...currentSessionRef.current, ...updated }, { preserveTranscript: transcriptDirty });
          setShowMatterSelector(false);
        }
      }
      toast.success('Matter created');
    } catch {
      toast.error('Could not create matter');
    } finally {
      setCreatingMatter(false);
    }
  };

  const deleteSession = async (sessionDoc) => {
    if (!sessionDoc?.session_id) return;
    const isCurrentSession = selectedSessionIdRef.current === sessionDoc.session_id || currentSessionRef.current?.session_id === sessionDoc.session_id;
    if (isCurrentSession && (recording || stopping || currentSessionRef.current?.status === 'recording')) {
      toast.error('Stop the current recording before moving this session to trash');
      return;
    }
    const label = sessionDoc.title || 'Untitled session';
    if (!window.confirm(`Move "${label}" to trash? You can permanently delete it later from Settings.`)) return;

    try {
      const response = await fetch(`${API}/jolly/sessions/${sessionDoc.session_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete session');

      let fallback = null;
      setSessions(prev => {
        const next = prev.filter(item => item.session_id !== sessionDoc.session_id);
        fallback = next[0] || null;
        return next;
      });

      if (isCurrentSession) {
        selectedSessionIdRef.current = '';
        currentSessionRef.current = null;
        setSelectedSessionId('');
        setCurrentSession(null);
        setEditedTranscript('');
        setTranscriptDirty(false);
        setSessionTitle('');
        setViewMode(fallback ? 'history' : 'home');
        if (fallback) {
          openSessionView(fallback, { fromHistory: true });
        } else {
          navigateToHome();
        }
      }

      toast.success(`"${label}" moved to trash`);
      loadMatters();
    } catch {
      toast.error('Failed to move session to trash');
    }
  };

  const appendSegments = useCallback((incomingSegments) => {
    setCurrentSession(prev => {
      if (!prev) return prev;
      const existing = (prev.segments || []).filter(
        segment => !incomingSegments.some(item => item.segment_id === segment.segment_id),
      );
      const merged = [...existing, ...incomingSegments].sort((a, b) => (a.sequence_index || 0) - (b.sequence_index || 0));
      const next = { ...prev, segments: merged };
      currentSessionRef.current = next;
      return next;
    });
  }, []);

  const queueChunkUpload = useCallback((blob, mimeType, chunkStartMs, durationMs) => {
    if (!selectedSessionIdRef.current) return Promise.resolve();
    const nextSequenceIndex = chunkSequenceRef.current;
    if (nextSequenceIndex > 0 && blob.size < MIN_RELIABLE_CHUNK_BYTES && durationMs < MIN_RELIABLE_CHUNK_MS) {
      return Promise.resolve();
    }
    const sessionId = selectedSessionIdRef.current;
    const sequenceIndex = chunkSequenceRef.current++;
    const fileName = `chunk_${sequenceIndex}${extensionForMimeType(mimeType)}`;

    uploadQueueRef.current = uploadQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const formData = new FormData();
        formData.append('file', blob, fileName);
        formData.append('sequence_index', String(sequenceIndex));
        formData.append('chunk_start_ms', String(chunkStartMs));
        formData.append('duration_ms', String(durationMs));

        const response = await fetch(`${API}/jolly/sessions/${sessionId}/chunks`, {
          method: 'POST',
          headers: (() => {
            const authHeaders = getAuthHeaders();
            return authHeaders.Authorization ? { Authorization: authHeaders.Authorization } : {};
          })(),
          credentials: 'include',
          body: formData,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.detail || 'Chunk upload failed');
        }
        const data = await response.json();
        appendSegments(data.segments || []);
        setCurrentSession(prev => {
          if (!prev) return prev;
          const next = {
            ...prev,
            live_transcript: data.live_transcript || prev.live_transcript,
            live_signals: data.live_signals || prev.live_signals,
            languages_detected: data.language_code ? Array.from(new Set([...(prev.languages_detected || []), data.language_code])) : prev.languages_detected,
            error_message: '',
            status: recordingRef.current ? 'recording' : (prev.status === 'recording' ? 'processing' : (prev.status || 'processing')),
          };
          currentSessionRef.current = next;
          return next;
        });
      })
      .catch(error => {
        setCurrentSession(prev => {
          if (!prev) return prev;
          const next = { ...prev, error_message: error.message || 'Audio upload failed' };
          currentSessionRef.current = next;
          return next;
        });
      });

    return uploadQueueRef.current;
  }, [appendSegments, getAuthHeaders]);

  const startCapture = useCallback((sessionDoc, { recorder, stream, mimeType }) => {
    mediaStreamRef.current = stream;
    mediaRecorderRef.current = recorder;
    uploadQueueRef.current = Promise.resolve();
    chunkSequenceRef.current = 0;
    nextChunkStartMsRef.current = 0;
    recordingStartedAtRef.current = Date.now();
    initChunkRef.current = null;
    setElapsedSeconds(0);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - recordingStartedAtRef.current) / 1000));
    }, 1000);

    recorder.ondataavailable = event => {
      if (!event.data || event.data.size === 0) return;
      const elapsedMs = Date.now() - recordingStartedAtRef.current;
      const chunkStartMs = nextChunkStartMsRef.current;
      const computedDuration = Math.max(300, elapsedMs - chunkStartMs);
      nextChunkStartMsRef.current = elapsedMs;
      const chunkMimeType = event.data.type || recorder.mimeType || mimeType || 'application/octet-stream';
      if (chunkSequenceRef.current === 0) {
        initChunkRef.current = event.data;
        void queueChunkUpload(event.data, chunkMimeType, chunkStartMs, computedDuration);
      } else {
        const initChunk = initChunkRef.current;
        const uploadBlob = initChunk
          ? new Blob([initChunk, event.data], { type: chunkMimeType })
          : event.data;
        void queueChunkUpload(uploadBlob, chunkMimeType, chunkStartMs, computedDuration);
      }
    };

    recorder.onstop = () => {
      if (mediaRecorderRef.current === recorder) {
        mediaRecorderRef.current = null;
      }
      stopMediaStream(stream);
    };

    recorder.start(CHUNK_TIMESLICE_MS);
    recordingRef.current = true;
    setRecording(true);
    toast.success('Jolly is listening');
  }, [queueChunkUpload, stopMediaStream]);

  const beginSession = async () => {
    const targetMatterId = selectedMatter?.matter_id || selectedMatterId;
    const targetMatterName = selectedMatter?.name || selectedMatterName || targetMatterId;
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      toast.error('This browser does not support microphone capture');
      return;
    }

    const pendingTitle = targetMatterName ? `Jolly capture · ${targetMatterName}` : 'Jolly capture';
    const classificationStatus = classificationTouched ? 'confirmed' : 'pending';

    setCreatingSession(true);
    let stream = null;
    let recorder = null;
    try {
      const supportedMimeType = getRecorderMimeType();
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorder = new MediaRecorder(stream, supportedMimeType ? { mimeType: supportedMimeType } : undefined);

      const response = await fetch(`${API}/jolly/sessions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          ...(targetMatterId ? { matter_id: targetMatterId, matter_name: targetMatterName } : {}),
          mode,
          title: sessionTitle.trim() || (classificationStatus === 'pending' ? pendingTitle : undefined),
          language_hint: languageHint,
          provider_name: 'sarvam',
          classification_status: classificationStatus,
        }),
      });
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!response.ok) throw new Error('Failed to create session');
      const sessionDoc = await response.json();
      const startResponse = await fetch(`${API}/jolly/sessions/${sessionDoc.session_id}/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!startResponse.ok) throw new Error('Failed to start session');
      const startedSession = await startResponse.json();
      const hydrated = { ...startedSession, segments: [], structured_outputs: [] };
      syncCurrentSession(hydrated);
      setViewMode('session');
      openSessionView(hydrated);
      startCapture(hydrated, { recorder, stream, mimeType: supportedMimeType });
      stream = null;
      recorder = null;
    } catch (error) {
      toast.error(error.message || 'Could not start Jolly');
    } finally {
      if (recorder?.state && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {}
      }
      stopMediaStream(stream);
      setCreatingSession(false);
    }
  };

  const stopCapture = async () => {
    if (!selectedSessionIdRef.current) return;
    setStopping(true);
    recordingRef.current = false;
    setRecording(false);
    setCurrentSession(prev => {
      if (!prev) return prev;
      const next = { ...prev, status: 'processing', error_message: '' };
      currentSessionRef.current = next;
      return next;
    });
    try {
      const recorder = mediaRecorderRef.current;
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (recorder?.state && recorder.state !== 'inactive') {
        await new Promise((resolve, reject) => {
          recorder.addEventListener('stop', resolve, { once: true });
          try {
            if (recorder.state === 'recording') {
              try {
                recorder.requestData();
              } catch {}
            }
            recorder.stop();
          } catch (error) {
            reject(error);
          }
        });
      }
      await uploadQueueRef.current.catch(() => undefined);
    } catch {
      // Some browsers throw on stop; continue to finalize.
    } finally {
      clearRecorderState();
    }

    try {
      const response = await fetch(`${API}/jolly/sessions/${selectedSessionIdRef.current}/finalize`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          title: sessionTitle.trim() || currentSessionRef.current?.title || undefined,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to finalize session');
      }
      const finalized = await response.json().catch(() => ({}));
      setCurrentSession(prev => {
        if (!prev) return prev;
        const next = {
          ...prev,
          status: finalized.status || 'processing',
          error_message: finalized.error_message || '',
        };
        currentSessionRef.current = next;
        return next;
      });
      if ((finalized.status || 'processing') === 'failed') {
        toast.error(finalized.error_message || 'Jolly could not transcribe that recording');
      } else {
        toast.success('Jolly is structuring the session');
      }
      await Promise.all([
        loadSessions(currentSessionRef.current?.matter_id || selectedMatterId),
        loadSessionDetail(selectedSessionIdRef.current, { silent: true }),
      ]);
    } catch (error) {
      setCurrentSession(prev => {
        if (!prev) return prev;
        const next = {
          ...prev,
          status: 'failed',
          error_message: error.message || prev.error_message,
        };
        currentSessionRef.current = next;
        return next;
      });
      toast.error(error.message || 'Could not stop recording cleanly');
    } finally {
      setStopping(false);
    }
  };

  const saveSessionMeta = async ({ classificationStatus, goHomeAfterSave = false } = {}) => {
    if (!currentSessionRef.current?.session_id) return;
    const previousSession = currentSessionRef.current;
    const nextClassificationStatus = classificationStatus || (classificationTouched ? 'confirmed' : sessionClassification);

    if (!sessionTitleDirty && !modeDirty && nextClassificationStatus === sessionClassification) {
      if (goHomeAfterSave) {
        toast.success('Session saved. You can classify it later.');
        openHomeView();
      }
      return;
    }

    setSavingSessionMeta(true);
    try {
      const response = await fetch(`${API}/jolly/sessions/${previousSession.session_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          title: sessionTitle.trim() || undefined,
          mode,
          classification_status: nextClassificationStatus,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to update session');
      }
      const updated = await response.json();
      const merged = {
        ...previousSession,
        ...updated,
        segments: previousSession.segments || [],
        structured_outputs: previousSession.structured_outputs || [],
      };
      syncCurrentSession(merged, { preserveTranscript: transcriptDirty });
      await loadSessions(merged.matter_id || selectedMatterId);

      const modeChanged = merged.mode !== previousSession.mode;
      if (merged.status === 'processing' && modeChanged) {
        toast.success('Jolly is re-structuring this session with the new classification');
      } else if (nextClassificationStatus === 'pending') {
        toast.success('Session saved. You can classify it later.');
      } else {
        toast.success('Session details saved');
      }

      if (goHomeAfterSave) openHomeView();
    } catch (error) {
      toast.error(error.message || 'Could not update this session');
    } finally {
      setSavingSessionMeta(false);
    }
  };

  const saveTranscript = async () => {
    if (!currentSession?.session_id || !editedTranscript.trim()) return;
    try {
      const response = await fetch(`${API}/jolly/sessions/${currentSession.session_id}/transcript`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ reviewed_transcript: editedTranscript }),
      });
      if (!response.ok) throw new Error('Failed to save transcript');
      setTranscriptDirty(false);
      toast.success('Reviewed transcript saved');

      await Promise.all([
        loadSessionDetail(currentSession.session_id, { silent: true }),
        loadSessions(currentSession.matter_id || selectedMatterId),
      ]);

      const updated = currentSessionRef.current;
      const outputs = updated?.structured_outputs || [];

      if (outputs.length > 0) {
        toast.success(`Jolly prepared ${outputs.length} memory candidate${outputs.length === 1 ? '' : 's'} for review`);
      } else {
        setAnalysisDialog({
          message: 'Nothing relevant was seen in this transcript. Do you want to keep the session anyway?',
          onConfirm: () => {
            setAnalysisDialog(null);
            toast.success('Session kept. You can review it later from past recordings.');
            openHomeView();
          },
          onCancel: async () => {
            setAnalysisDialog(null);
            if (currentSessionRef.current?.session_id) {
              await deleteSession(currentSessionRef.current);
            }
          },
        });
      }
    } catch {
      toast.error('Could not save transcript');
    }
  };

  const linkMatterToSession = async (matterId) => {
    if (!currentSession?.session_id || !matterId) return;
    try {
      const response = await fetch(`${API}/jolly/sessions/${currentSession.session_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ matter_id: matterId }),
      });
      if (!response.ok) throw new Error('Failed to link matter');
      const updated = await response.json();
      syncCurrentSession({ ...currentSession, ...updated }, { preserveTranscript: transcriptDirty });
      await loadSessions(matterId);
      toast.success('Session linked to matter');
    } catch {
      toast.error('Could not link session to matter');
    }
  };

  const updateOutput = async (structuredOutputId, payload) => {
    try {
      const response = await fetch(`${API}/jolly/structured-outputs/${structuredOutputId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to update item');
      const updated = await response.json();
      toast.success('Memory choice saved');
      if (currentSession?.session_id) {
        await Promise.all([
          loadSessionDetail(currentSession.session_id, { silent: true }),
          loadSessions(currentSession.matter_id || selectedMatterId),
        ]);
      }
      return updated.collaboration || {};
    } catch {
      toast.error('Could not update structured item');
      throw new Error('Could not update structured item');
    }
  };

  const jumpToCitation = citation => {
    if (!citation?.transcript_segment_id) return;
    const node = document.getElementById(`segment-${citation.transcript_segment_id}`);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.animate(
        [
          { boxShadow: '0 0 0 0 rgba(46, 204, 90, 0.0)' },
          { boxShadow: '0 0 0 3px rgba(46, 204, 90, 0.32)' },
          { boxShadow: '0 0 0 0 rgba(46, 204, 90, 0.0)' },
        ],
        { duration: 1200, easing: 'ease-out' },
      );
    }
  };

  const openMatterChat = () => {
    if (!currentSession?.matter_id) return;
    const params = new URLSearchParams({
      matter_id: currentSession.matter_id,
      matter_name: currentSession.matter_name || currentSession.matter_id,
    });
    navigate(`/chat?${params.toString()}`);
  };

  const openResearch = content => {
    const params = new URLSearchParams({
      prefill_query: content,
      prefill_type: 'TOPIC',
    });
    if (currentSession?.matter_id) params.set('matter_id', currentSession.matter_id);
    if (currentSession?.matter_name) params.set('matter_name', currentSession.matter_name);
    navigate(`/research?${params.toString()}`);
  };

  useEffect(() => {
    document.title = 'Jolly — Advoq';
  }, []);

  useEffect(() => {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (selectedMatter?.name) {
      setSelectedMatterName(selectedMatter.name);
    }
  }, [selectedMatter]);

  useEffect(() => {
    selectedSessionIdRef.current = selectedSessionId;
  }, [selectedSessionId]);

  useEffect(() => {
    currentSessionRef.current = currentSession;
  }, [currentSession]);

  useEffect(() => {
    recordingRef.current = recording;
  }, [recording]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const matterId = params.get('matter_id');
    const matterName = params.get('matter_name');
    const sessionId = params.get('session_id');
    if (matterId) setSelectedMatterId(matterId);
    if (matterName) setSelectedMatterName(matterName);
    if (sessionId) {
      setSelectedSessionId(sessionId);
      setViewMode(prev => prev === 'history' ? 'history' : 'session');
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const journeyKey = params.get('journey_key');
    if (!journeyKey) return;
    recordJourneyVisit(journeyKey, {
      surface: 'JOLLY',
      label: 'Jolly',
      href: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    loadMatters();
  }, [loadMatters]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (selectedSessionId) {
      loadSessionDetail(selectedSessionId);
    }
  }, [selectedSessionId, loadSessionDetail]);

  useEffect(() => {
    const handler = setTimeout(() => {
      loadSessions();
    }, 250);
    return () => clearTimeout(handler);
  }, [historyQuery, selectedMatterId, loadSessions]);

  useEffect(() => {
    const shouldPoll = currentSession && !stopping && ['processing', 'recording'].includes(currentSession.status);
    if (!shouldPoll) return undefined;
    const interval = window.setInterval(() => {
      loadSessionDetail(currentSession.session_id, { silent: true });
      loadSessions(currentSession.matter_id);
    }, 3500);
    return () => window.clearInterval(interval);
  }, [currentSession, loadSessionDetail, loadSessions, stopping]);

  useEffect(() => () => {
    if (mediaRecorderRef.current?.state && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {}
    }
    clearRecorderState();
  }, [clearRecorderState]);

  const contextMatterId = currentSession?.matter_id || selectedMatterId || '';
  const contextMatterName = currentSession?.matter_name || selectedMatterName || selectedMatter?.name || contextMatterId;
  const matterContextItems = contextMatterId ? [
    { key: 'overview', onClick: () => navigate(`/war-room/${contextMatterId}`) },
    { key: 'draft', onClick: () => navigate(withQueryParams('/documents', { matter_id: contextMatterId, matter_name: contextMatterName })) },
    { key: 'files', onClick: () => navigate(withQueryParams('/research', { matter_id: contextMatterId, matter_name: contextMatterName, context_panel: 'files' })) },
    { key: 'jolly', badge: currentSession?.session_id ? 'Live' : '', onClick: () => navigate(withQueryParams('/jolly', { matter_id: contextMatterId, matter_name: contextMatterName })) },
    { key: 'review', onClick: () => navigate(`/war-room/${contextMatterId}?view=agents`) },
  ] : [];

  const renderHomeStage = () => {
    const canRecord = true;

    return (
      <div data-testid="jolly-entry-screen" style={stageStackStyle}>
        <section style={entryShellStyle}>
          <header style={entryHeaderStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', textAlign: 'center' }}>
              <div style={entryEyebrowStyle}>
                <Mic size={14} />
                <span>Jolly {user?.plan === 'FREE' ? 'Early Access' : 'Premium'}</span>
              </div>
              <h1 style={entryTitleStyle}>Jolly</h1>
              <p style={entryCopyStyle}>
                Capture, review, and keep what belongs.
              </p>
            </div>

          </header>

          <div style={entryMicStageStyle(currentMode.accent)}>
            <div style={entryOrbStyle(currentMode.accent, '0%', 260, 0.16)} />
            <div style={entryOrbStyle('#f59e0b', '100%', 200, 0.12)} />
            <button
              type="button"
              data-testid="jolly-primary-record"
              onClick={beginSession}
              disabled={!canRecord || creatingSession || stopping}
              style={recordButtonStyle({
                accent: currentMode.accent,
                disabled: !canRecord || creatingSession || stopping,
              })}
            >
              {creatingSession ? (
                <Loader2 size={48} className="animate-spin" />
              ) : (
                <Mic size={56} strokeWidth={2.2} />
              )}
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '1.05rem', fontWeight: 700 }}>
                {creatingSession ? 'Starting…' : 'Tap once and start recording'}
              </div>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.7 }}>
                Matter and classification can be set before or after recording.
              </p>
            </div>
          </div>

          <footer style={entryFooterStyle}>
            <button type="button" className="advoq-btn-ghost" onClick={openHistoryView}>
              View past recordings
            </button>
            <button
              type="button"
              className="advoq-btn-ghost"
              onClick={() => setShowSetup(prev => !prev)}
            >
              {showSetup ? 'Hide context' : 'Give Jolly Context'}
            </button>
          </footer>
        </section>

        {showSetup ? (
          <section style={{ ...panelStyle, maxWidth: '980px', margin: '0 auto', width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '18px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <p style={eyebrowStyle}>Matter</p>
                  <select
                    value={selectedMatterId}
                    onChange={event => {
                      const nextId = event.target.value;
                      const nextMatter = matters.find(item => item.matter_id === nextId);
                      setSelectedMatterId(nextId);
                      setSelectedMatterName(nextMatter?.name || nextId);
                    }}
                    className="advoq-input"
                    style={{ width: '100%', fontSize: '13px' }}
                  >
                    <option value="">Link to a matter (optional)</option>
                    {matters.map(matter => (
                      <option key={matter.matter_id} value={matter.matter_id}>
                        {matter.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={newMatterName}
                    onChange={event => setNewMatterName(event.target.value)}
                    placeholder="Create a new matter"
                    className="advoq-input"
                    style={{ flex: 1, fontSize: '13px' }}
                  />
                  <button type="button" onClick={createMatter} disabled={creatingMatter || !newMatterName.trim()} className="advoq-btn-primary" style={{ minWidth: '44px' }}>
                    {creatingMatter ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                  <input
                    value={sessionTitle}
                    onChange={event => setSessionTitle(event.target.value)}
                    className="advoq-input"
                    placeholder={selectedMatter?.name ? `Jolly capture · ${selectedMatter.name}` : 'Optional session title'}
                    style={{ fontSize: '13px' }}
                  />
                  <select
                    value={languageHint}
                    onChange={event => setLanguageHint(event.target.value)}
                    className="advoq-input"
                    style={{ fontSize: '13px' }}
                  >
                    {LANGUAGE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <p style={eyebrowStyle}>Optional classification</p>
                  <h2 style={sectionTitleStyle}>Choose a session type now or leave it for later</h2>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px' }}>
                  {JOLLY_MODES.map(item => (
                    <ModeChoiceCard
                      key={item.value}
                      item={item}
                      active={mode === item.value}
                      compact
                      onClick={() => {
                        setMode(item.value);
                        setClassificationTouched(true);
                      }}
                    />
                  ))}
                </div>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                  Jolly can start immediately without this. If you classify later, it can re-structure the session after the recording is saved.
                </p>
              </div>
            </div>
          </section>
        ) : null}
      </div>
    );
  };

  const renderRecordingStage = () => {
    const displayMatter = currentSession?.matter_name || selectedMatterName || selectedMatter?.name || 'Current matter';
    const pending = classificationState(currentSession) === 'pending';

    return (
      <div data-testid="jolly-recording-stage" style={stageStackStyle}>
        <section style={recordingHeroStyle(sessionModeMeta.accent)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center', textAlign: 'center' }}>
            <div style={entryEyebrowStyle}>
              <Mic size={14} />
              <span>Recording now</span>
            </div>
            <h1 style={{ margin: 0, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.4rem)' }}>
              {formatDuration(elapsedSeconds)}
            </h1>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7, maxWidth: '620px' }}>
              {pending
                ? 'Jolly is capturing first. You can classify this session after you stop.'
                : `Recording a ${sessionModeMeta.label.toLowerCase()} for ${displayMatter}.`}
            </p>

            <button
              type="button"
              data-testid="jolly-stop-recording"
              onClick={stopCapture}
              disabled={stopping}
              style={{
                position: 'relative',
                width: '92px',
                height: '92px',
                borderRadius: '50%',
                border: 'none',
                background: 'radial-gradient(circle, #ef4444 0%, #dc2626 100%)',
                color: '#fff',
                cursor: stopping ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 0 rgba(239,68,68,0.3), 0 14px 36px rgba(239,68,68,0.32)',
                opacity: stopping ? 0.7 : 1,
              }}
            >
              <span className="jolly-mic-ring" />
              <span className="jolly-mic-ring" />
              <span className="jolly-mic-ring" />
              {stopping ? <Loader2 size={26} className="animate-spin" style={{ position: 'relative', zIndex: 1 }} /> : <Mic size={28} style={{ position: 'relative', zIndex: 1 }} />}
            </button>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '24px', color: '#f87171' }}>
              <span className="jolly-sound-bar" />
              <span className="jolly-sound-bar" />
              <span className="jolly-sound-bar" />
              <span className="jolly-sound-bar" />
              <span className="jolly-sound-bar" />
            </div>
            <div style={{ fontSize: '12px', color: '#f87171', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Tap to stop
            </div>
          </div>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <section style={panelStyle}>
            <div style={{ marginBottom: '14px' }}>
              <p style={eyebrowStyle}>Live transcript</p>
              <h2 style={sectionTitleStyle}>Jolly is listening in real time</h2>
            </div>
            {currentSession?.live_transcript ? (
              <div style={recordingPreviewStyle}>
                {currentSession.live_transcript}
              </div>
            ) : (
              <EmptyPanel copy="Transcript text will appear here as audio chunks are uploaded and transcribed." compact />
            )}
            {currentSession?.error_message ? (
              <p style={{ margin: '12px 0 0', color: '#ef4444', fontSize: '12px' }}>{currentSession.error_message}</p>
            ) : null}
          </section>

          <section style={panelStyle}>
            <div style={{ marginBottom: '14px' }}>
              <p style={eyebrowStyle}>Live signals</p>
              <h2 style={sectionTitleStyle}>What Jolly is noticing</h2>
            </div>
            {(currentSession?.live_signals || []).length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {currentSession.live_signals.map((signal, index) => (
                  <div key={`${signal.kind}-${index}`} style={signalCardStyle}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '6px' }}>
                      {signal.kind}
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.7 }}>
                      {signal.value}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyPanel copy="Dates, issues, action cues, and authority references will surface here while you speak." compact />
            )}
          </section>
        </div>
      </div>
    );
  };

  const renderSessionWorkbench = ({ embedded = false } = {}) => {
    if (!currentSession && !loadingSession) {
      return <EmptyPanel copy="Choose a session to review its transcript, classification, and memory decisions." />;
    }

    if (loadingSession && !currentSession) {
      return <EmptyPanel copy="Loading the selected Jolly session…" />;
    }

    const transcriptReviewed = Boolean(currentSession?.transcript_reviewed_by_user);
    const knowledgeReady = transcriptReviewed && Boolean(currentSession?.knowledge_file_id) && currentSession?.status === 'ready';
    const agentsReady = transcriptReviewed && retention.accepted > 0;
    const translationMeta = currentSession?.translation_meta || {};
    const detectedLanguages = currentSession?.detected_languages || currentSession?.languages_detected || [];
    const primaryDetectedLanguage = currentSession?.detected_language || detectedLanguages[0] || '';
    const classificationPending = sessionClassification === 'pending';
    const CurrentStatusIcon = currentStatus.icon;

    return (
      <div data-testid="jolly-session-workbench" style={stageStackStyle}>
        {!embedded ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <button type="button" className="advoq-btn-ghost" onClick={openHomeView}>
              <ArrowLeft size={14} /> Back to Jolly
            </button>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" className="advoq-btn-ghost" onClick={openHistoryView}>
                View past recordings
              </button>
              {currentSession?.matter_id ? (
                <button type="button" className="advoq-btn-ghost" onClick={openMatterChat}>
                  Open matter chat
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <section style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ maxWidth: '760px' }}>
              <p style={eyebrowStyle}>{currentSession?.matter_name || currentSession?.matter_id || 'Jolly session'}</p>
              <h1 style={{ margin: 0, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
                {currentSession?.title || 'Untitled session'}
              </h1>
              <p style={{ margin: '10px 0 0', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                {sessionSummary || 'Review the transcript, confirm the classification, and decide what should stay in long-term memory.'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={statusChipStyle(currentStatus.tone, currentStatus.color)}>
                <CurrentStatusIcon size={11} className={currentStatus.icon === Loader2 ? 'animate-spin' : ''} />
                {currentStatus.label}
              </span>
              <span style={smallChipStyle}>
                {classificationPending ? 'Classification pending' : sessionModeMeta.label}
              </span>
              <span style={smallChipStyle}>
                {retention.accepted} kept
              </span>
              <span style={smallChipStyle}>
                {transcriptReviewed ? 'Transcript reviewed' : 'Transcript review pending'}
              </span>
            </div>
          </div>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.8fr)', gap: '20px' }}>
          <section style={panelStyle}>
            <div style={{ marginBottom: '16px' }}>
              <p style={eyebrowStyle}>Review & classify</p>
              <h2 style={sectionTitleStyle}>Confirm what this session was</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <input
                value={sessionTitle}
                onChange={event => setSessionTitle(event.target.value)}
                className="advoq-input"
                placeholder="Session title"
                style={{ fontSize: '13px' }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                {JOLLY_MODES.map(item => (
                  <ModeChoiceCard
                    key={item.value}
                    item={item}
                    active={mode === item.value}
                    compact
                    onClick={() => {
                      setMode(item.value);
                      setClassificationTouched(true);
                    }}
                  />
                ))}
              </div>

              <p style={{ margin: 0, color: classificationPending ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                {classificationPending
                  ? 'This capture is still unclassified. Save a type now, or leave it for later. If you change the type, Jolly will re-structure the session.'
                  : 'Changing the session type later is safe. Jolly will re-structure the transcript and memory candidates using the new classification.'}
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button
                  type="button"
                  className="advoq-btn-primary"
                  disabled={savingSessionMeta || !currentSession}
                  onClick={() => saveSessionMeta({ classificationStatus: 'confirmed' })}
                >
                  {savingSessionMeta ? <Loader2 size={15} className="animate-spin" /> : 'Save classification'}
                </button>
                <button
                  type="button"
                  className="advoq-btn-ghost"
                  disabled={savingSessionMeta || !currentSession}
                  onClick={() => {
                    if (!sessionTitleDirty && !modeDirty && classificationPending) {
                      toast.success('Session saved. You can classify it later.');
                      openHomeView();
                      return;
                    }
                    saveSessionMeta({ classificationStatus: 'pending', goHomeAfterSave: true });
                  }}
                >
                  Save for later
                </button>
              </div>
            </div>
          </section>

          <section style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={panelStyle}>
              <div style={{ marginBottom: '16px' }}>
                <p style={eyebrowStyle}>Long-term knowledge</p>
                <h2 style={sectionTitleStyle}>Where this session lives</h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={provenanceRowStyle}>
                  <span>Transcript archive</span>
                  <strong>{knowledgeReady ? 'Indexed' : transcriptReviewed ? (currentSession?.status === 'processing' ? 'Indexing…' : 'Queued') : 'Waiting for review'}</strong>
                </div>
                <div style={provenanceRowStyle}>
                  <span>Retained memory items</span>
                  <strong>{retention.accepted}</strong>
                </div>
                <div style={provenanceRowStyle}>
                  <span>Visible to draft, files, and review</span>
                  <strong>{agentsReady ? `${retention.accepted} kept item${retention.accepted === 1 ? '' : 's'}` : 'Not yet'}</strong>
                </div>
                <div style={provenanceRowStyle}>
                  <span>Session ID</span>
                  <strong style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{currentSession?.session_id}</strong>
                </div>
              </div>

              {/* Matter assignment */}
              <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--border-subtle)' }}>
                <p style={{ ...eyebrowStyle, marginBottom: '8px' }}>Linked matter</p>
                {currentSession?.matter_id && !showMatterSelector ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>
                      {currentSession.matter_name || currentSession.matter_id}
                    </span>
                    <button
                      type="button"
                      className="app-chat-button"
                      onClick={() => setShowMatterSelector(true)}
                      style={{ fontSize: '11px' }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {!currentSession?.matter_id && (
                      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                        Not linked to a matter yet. Assign it so it stays searchable inside the matter workspace and available to review.
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <select
                        className="advoq-input"
                        style={{ flex: '1 1 180px', fontSize: '12px' }}
                        value=""
                        onChange={event => {
                          if (event.target.value) {
                            linkMatterToSession(event.target.value);
                            setShowMatterSelector(false);
                          }
                        }}
                      >
                        <option value="">Choose existing matter…</option>
                        {matters.map(m => (
                          <option key={m.matter_id} value={m.matter_id}>{m.name || m.title}</option>
                        ))}
                      </select>
                      {showMatterSelector && (
                        <button type="button" className="app-chat-button" onClick={() => setShowMatterSelector(false)}>
                          Cancel
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        placeholder="Or type a new matter name"
                        className="advoq-input"
                        style={{ flex: 1, fontSize: '12px' }}
                        value={newMatterName}
                        onChange={event => setNewMatterName(event.target.value)}
                        onKeyDown={async event => {
                          if (event.key === 'Enter' && newMatterName.trim()) {
                            await createMatter();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="advoq-btn-primary"
                        style={{ minWidth: '44px' }}
                        disabled={creatingMatter || !newMatterName.trim()}
                        onClick={async () => { await createMatter(); }}
                      >
                        {creatingMatter ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <p style={{ margin: '14px 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                Reviewed transcript text can be archived for the matter, but only kept items compound into downstream agent memory.
              </p>
            </div>

            <div style={panelStyle}>
              <div style={{ marginBottom: '16px' }}>
                <p style={eyebrowStyle}>Status</p>
                <h2 style={sectionTitleStyle}>Review progress</h2>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <span style={smallChipStyle}>
                  {currentSession?.transcript_reviewed_by_user ? 'Transcript saved' : 'Transcript not confirmed'}
                </span>
                <span style={smallChipStyle}>
                  {classificationPending ? 'Classification pending' : 'Classification confirmed'}
                </span>
                <span style={smallChipStyle}>
                  {retention.pending} waiting on a memory decision
                </span>
                <span style={smallChipStyle}>
                  {retention.discarded} discarded
                </span>
              </div>
              {!transcriptReviewed ? (
                <p style={{ margin: '14px 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                  Nothing from this session reaches other agents until you confirm the transcript and explicitly keep the memory items that belong in the matter.
                </p>
              ) : retention.accepted === 0 ? (
                <p style={{ margin: '14px 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                  The transcript is reviewed, but no memory items are kept yet. Review the structured outputs below to decide what should compound into the matter.
                </p>
              ) : null}
            </div>
          </section>
        </div>

        <section style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div>
              <p style={eyebrowStyle}>Transcript</p>
              <h2 style={sectionTitleStyle}>Review the capture</h2>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Transcript view tabs */}
              {['original', 'english', 'hindi'].map(view => (
                <button
                  key={view}
                  type="button"
                  onClick={() => setTranscriptView(view)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${transcriptView === view ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                    background: transcriptView === view ? 'var(--accent-glow)' : 'transparent',
                    color: transcriptView === view ? 'var(--accent-primary)' : 'var(--text-muted)',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-ui)',
                    textTransform: 'capitalize',
                  }}
                >
                  {view === 'original' ? 'Original' : view === 'english' ? 'English' : 'हिन्दी'}
                </button>
              ))}
              {/* Translate button (shows on English/Hindi view when no translation) */}
              {(transcriptView === 'english' && !editedEnglishTranscript) || (transcriptView === 'hindi' && !editedHindiTranscript) ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (!currentSession?.session_id) return;
                    setTranslating(true);
                    try {
                      const token = localStorage.getItem('session_token');
                      const resp = await fetch(`${API}/jolly/sessions/${currentSession.session_id}/translate`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                      });
                      if (!resp.ok) throw new Error('Translation failed');
                      const data = await resp.json();
                      setEditedEnglishTranscript(data.english_transcript || '');
                      setEditedHindiTranscript(data.hindi_transcript || '');
                      toast.success('Transcripts translated. You can now edit both versions.');
                    } catch {
                      toast.error('Translation failed. Please try again.');
                    } finally {
                      setTranslating(false);
                    }
                  }}
                  disabled={translating}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--accent-primary)',
                    background: 'var(--accent-primary)',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  {translating ? <><Loader2 size={11} className="animate-spin" /> Translating…</> : <><Languages size={11} /> Generate {transcriptView === 'english' ? 'English' : 'Hindi'}</>}
                </button>
              ) : null}
              {/* Save translated transcript */}
              {translatedDirty && transcriptView !== 'original' ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (!currentSession?.session_id) return;
                    try {
                      const token = localStorage.getItem('session_token');
                      const body = transcriptView === 'english'
                        ? { english_transcript: editedEnglishTranscript }
                        : { hindi_transcript: editedHindiTranscript };
                      const resp = await fetch(`${API}/jolly/sessions/${currentSession.session_id}/translated-transcript`, {
                        method: 'PUT',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify(body),
                      });
                      if (!resp.ok) throw new Error('Save failed');
                      setTranslatedDirty(false);
                      toast.success('Translated transcript saved');
                    } catch {
                      toast.error('Could not save translated transcript');
                    }
                  }}
                  className="advoq-btn-ghost"
                >
                  Save {transcriptView} edits
                </button>
              ) : null}
              {transcriptView === 'original' ? (
                <button
                  type="button"
                  onClick={saveTranscript}
                  disabled={!editedTranscript.trim()}
                  className="advoq-btn-ghost"
                  style={{ opacity: editedTranscript.trim() ? 1 : 0.45 }}
                >
                  {transcriptDirty ? 'Save reviewed transcript' : 'Confirm transcript'}
                </button>
              ) : null}
            </div>
          </div>

          {(primaryDetectedLanguage || detectedLanguages.length || translationMeta?.views_available?.length) ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {primaryDetectedLanguage ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <Languages size={12} style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                    Source: <strong style={{ color: 'var(--text-primary)' }}>{formatDetectedLanguage(primaryDetectedLanguage)}</strong>
                    {detectedLanguages.length > 1 ? ` + ${detectedLanguages.slice(1).map(formatDetectedLanguage).join(', ')}` : ''}
                  </span>
                </div>
              ) : null}
              {translationMeta?.views_available?.length ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <Languages size={12} style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                    Original preserved · bilingual review views ready
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {currentSession?.status === 'processing' && !transcriptValue ? (
            <EmptyPanel copy="Jolly is transcribing and structuring this recording. You can leave this page and come back once it is ready." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Original transcript view */}
              {transcriptView === 'original' && (
                <>
                  <textarea
                    value={editedTranscript}
                    onChange={event => {
                      setEditedTranscript(event.target.value);
                      setTranscriptDirty(true);
                    }}
                    placeholder="The reviewed transcript will appear here."
                    style={transcriptEditorStyle(transcriptDirty)}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      This reviewed transcript is the lawyer-confirmed source text. The raw recording is preserved separately.
                    </span>
                    {currentSession?.error_message ? (
                      <span style={{ color: '#ef4444', fontSize: '12px' }}>{currentSession.error_message}</span>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '48vh', overflowY: 'auto', paddingRight: '4px' }}>
                    {(currentSession?.segments || []).length ? (
                      currentSession.segments.map(segment => (
                        <TranscriptSegment key={segment.segment_id} segment={segment} />
                      ))
                    ) : (
                      <EmptyPanel copy="Transcript segments will appear here once Jolly has processed the recording." compact />
                    )}
                  </div>
                  {currentSession?.live_transcript ? (
                    <div>
                      <button type="button" onClick={() => setShowOriginalTranscript(prev => !prev)} style={transcriptToggleStyle}>
                        <Languages size={13} />
                        {showOriginalTranscript ? 'Hide' : 'Show'} raw transcript
                      </button>
                      {showOriginalTranscript ? <div style={recordingPreviewStyle}>{currentSession.live_transcript}</div> : null}
                    </div>
                  ) : null}
                </>
              )}

              {/* English transcript view */}
              {transcriptView === 'english' && (
                editedEnglishTranscript ? (
                  <>
                    <textarea
                      value={editedEnglishTranscript}
                      onChange={e => { setEditedEnglishTranscript(e.target.value); setTranslatedDirty(true); }}
                      placeholder="English version of the transcript."
                      style={transcriptEditorStyle(translatedDirty)}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>English working view for legal review. The original reviewed transcript remains the canonical source.</span>
                  </>
                ) : (
                  <EmptyPanel copy="Click 'Generate English' above to create the English version of this transcript." compact />
                )
              )}

              {/* Hindi transcript view */}
              {transcriptView === 'hindi' && (
                editedHindiTranscript ? (
                  <>
                    <textarea
                      value={editedHindiTranscript}
                      onChange={e => { setEditedHindiTranscript(e.target.value); setTranslatedDirty(true); }}
                      placeholder="हिन्दी संस्करण यहाँ दिखेगा।"
                      style={{ ...transcriptEditorStyle(translatedDirty), fontFamily: "'Noto Sans Devanagari', 'Noto Sans', sans-serif", direction: 'ltr' }}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>हिन्दी समीक्षा दृश्य. मूल reviewed transcript सुरक्षित रहता है.</span>
                  </>
                ) : (
                  <EmptyPanel copy="Click 'Generate हिन्दी' above to create the Hindi version of this transcript." compact />
                )
              )}
            </div>
          )}
        </section>

        <section style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div>
              <p style={eyebrowStyle}>Select what stays</p>
              <h2 style={sectionTitleStyle}>Keep only what belongs in long-term memory</h2>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span style={smallChipStyle}>{retention.accepted} kept</span>
              <span style={smallChipStyle}>{retention.pending} pending</span>
              <span style={smallChipStyle}>{retention.discarded} discarded</span>
            </div>
          </div>

          <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.75 }}>
            Only kept items enter long-term matter memory. Everything else stays in Jolly as review context and does not compound into other agents.
          </p>

          {currentSession?.status === 'processing' ? (
            <EmptyPanel copy="Jolly is still extracting structured memory candidates from this session." compact />
          ) : groupedOutputs.length === 0 ? (
            <EmptyPanel
              title="No structured memory yet"
              copy="Jolly hasn’t surfaced retained facts, issues, or action items for this session yet."
              compact
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {groupedOutputs.map(section => {
                const SectionIcon = section.icon;
                return (
                  <div key={section.key} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '10px',
                        background: 'var(--bg-elevated)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent-primary)',
                      }}>
                        <SectionIcon size={14} />
                      </span>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
                          {section.label}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                          {section.items.length} item{section.items.length === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>

                    {section.items.map(item => (
                      <OutputCard
                        key={item.structured_output_id}
                        output={item}
                        matterId={currentSession?.matter_id || selectedMatterId}
                        onReview={updateOutput}
                        onOpenResearch={openResearch}
                        onJumpToCitation={jumpToCitation}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  };

  const renderHistoryStage = () => (
    <div data-testid="jolly-history-stage" style={stageStackStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <button type="button" className="advoq-btn-ghost" onClick={openHomeView}>
          <ArrowLeft size={14} /> Back to Jolly
        </button>
        <button type="button" className="advoq-btn-ghost" onClick={beginSession} disabled={creatingSession || stopping}>
          <Mic size={14} /> New recording
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
        <aside style={panelStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <p style={eyebrowStyle}>Past recordings</p>
              <h2 style={sectionTitleStyle}>Find a saved session</h2>
            </div>

            <select
              value={selectedMatterId}
              onChange={event => {
                const nextId = event.target.value;
                const nextMatter = matters.find(item => item.matter_id === nextId);
                setSelectedMatterId(nextId);
                setSelectedMatterName(nextMatter?.name || nextId);
              }}
              className="advoq-input"
              style={{ width: '100%', fontSize: '13px' }}
            >
              <option value="">All matters</option>
              {matters.map(matter => (
                <option key={matter.matter_id} value={matter.matter_id}>
                  {matter.name}
                </option>
              ))}
            </select>

            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', top: '12px', left: '12px', color: 'var(--text-muted)' }} />
              <input
                value={historyQuery}
                onChange={event => setHistoryQuery(event.target.value)}
                placeholder="Search recordings"
                className="advoq-input"
                style={{ width: '100%', paddingLeft: '36px', fontSize: '13px' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '72vh', overflowY: 'auto', paddingRight: '4px' }}>
              {loading ? (
                <JollySessionListSkeleton />
              ) : sessions.length === 0 ? (
                <EmptyPanel
                  title="No recordings found"
                  copy="Try a different matter filter or start a new recording to build this history."
                  compact
                />
              ) : (
                sessions.map(session => (
                  <SessionListItem
                    key={session.session_id}
                    session={session}
                    isActive={selectedSessionId === session.session_id}
                    onClick={() => openSessionView(session, { fromHistory: true })}
                    onDelete={() => deleteSession(session)}
                  />
                ))
              )}
            </div>
          </div>
        </aside>

        <section style={{ minWidth: 0 }}>
          {currentSession ? renderSessionWorkbench({ embedded: true }) : (
            <EmptyPanel copy="Select a recording to review its transcript, classification, and retained memory items." />
          )}
        </section>
      </div>
    </div>
  );

  const jollyAllowed = hasJollyAccess(user);

  let content = renderHomeStage();
  if (!jollyAllowed || accessDenied) {
    content = (
      <section
        className="advoq-card"
        style={{
          padding: '30px',
          borderRadius: '24px',
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          background: 'linear-gradient(135deg, color-mix(in srgb, #f59e0b 10%, var(--bg-surface)), color-mix(in srgb, var(--bg-elevated) 92%, transparent))',
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--app-panel-shadow-soft)',
        }}
      >
        <Lock size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '4px' }} />
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)', marginBottom: '14px', border: '1px solid var(--border-subtle)' }}>
            <Mic size={14} />
            <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
              Premium capture layer
            </span>
          </div>
          <h1 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '28px', fontFamily: 'var(--font-display)' }}>
            Jolly is a premium feature.
          </h1>
          <p style={{ margin: '12px 0 0', color: 'var(--text-secondary)', fontSize: '15px', lineHeight: 1.8, maxWidth: '820px' }}>
            Jolly captures client calls, case mocks, and personal thoughts, then retains the facts that matter inside the matter workspace.
          </p>
          <p style={{ margin: '16px 0 8px', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Want free access forever?{' '}
            <button
              type="button"
              onClick={() => setShowEarlyBird(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', fontWeight: 700, fontSize: '14px', padding: 0, textDecoration: 'underline' }}
            >
              Join the Early Believer Community.
            </button>
          </p>
        </div>
      </section>
    );
  } else if (showRecordingSurface) {
    content = renderRecordingStage();
  } else if (viewMode === 'history') {
    content = renderHistoryStage();
  } else if (viewMode === 'session' && (currentSession || loadingSession || selectedSessionId)) {
    content = renderSessionWorkbench();
  }

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="jolly-page">
        <div className="p-6" style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1320px', width: '100%', margin: '0 auto' }}>
          <div className="app-chat-header shrink-0" style={{ padding: '0 0 4px' }}>
            <div className="app-chat-header__row">
              <div className="app-chat-toolbar">
                <div className="app-chat-toolbar__group">
                  <span className="app-chat-button is-active">
                    <Mic size={13} /> Jolly
                  </span>
                </div>
                <div className="app-chat-toolbar__group ml-auto">
                  <FeatureHistoryMenu feature="jolly" matterId={selectedMatterId || ''} label="Recent Jolly" />
                </div>
              </div>
            </div>
          </div>
          {contextMatterId ? (
            <MatterWorkspaceContext
              matterName={contextMatterName}
              matterId={contextMatterId}
              objective={currentSession?.title || 'Capture and review spoken work without leaving the matter.'}
              items={matterContextItems}
              activeKey="jolly"
              compact
              caption="Matter context"
              testId="matter-workspace-context"
            />
          ) : null}
          {content}
        </div>

        {showEarlyBird && <EarlyBirdModal onClose={() => setShowEarlyBird(false)} onAccessGranted={() => window.location.reload()} />}

        {analysisDialog ? (
          <div style={dialogBackdropStyle}>
            <div style={dialogCardStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <span style={dialogIconStyle}>
                  <AlertTriangle size={18} />
                </span>
                <div>
                  <p style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px', lineHeight: 1.4 }}>
                    Nothing relevant detected
                  </p>
                  <p style={{ margin: '8px 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                    {analysisDialog.message}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={analysisDialog.onCancel}
                  className="advoq-btn-ghost"
                  style={{ color: '#ef4444' }}
                >
                  No, delete session
                </button>
                <button
                  type="button"
                  onClick={analysisDialog.onConfirm}
                  className="advoq-btn-primary"
                >
                  Yes, keep it
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

function JollySessionListSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[0, 1, 2].map((item) => (
        <div
          key={`jolly-session-skeleton-${item}`}
          className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-4"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

function EmptyPanel({ title = 'Nothing here yet', copy, compact = false, icon: Icon = Mic }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-10' : 'py-16'}`}>
      <Icon className={`${compact ? 'w-10 h-10' : 'w-12 h-12'} text-gray-300 mb-4`} />
      <h3 className={`${compact ? 'text-base' : 'text-lg'} font-medium text-gray-600 mb-2`}>{title}</h3>
      <p className="text-sm text-gray-400 max-w-xs">{copy}</p>
    </div>
  );
}

const outputActionStyle = {
  padding: '7px 11px',
  borderRadius: '999px',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-elevated)',
  color: 'var(--text-secondary)',
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
};

const citationButtonStyle = {
  border: '1px solid var(--border-default)',
  background: 'var(--bg-elevated)',
  color: 'var(--text-secondary)',
  borderRadius: '999px',
  padding: '6px 10px',
  fontSize: '11px',
  cursor: 'pointer',
};

const typeChipStyle = {
  fontSize: '10px',
  padding: '4px 8px',
  borderRadius: '999px',
  background: 'var(--bg-elevated)',
  color: 'var(--text-secondary)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

const smallChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 9px',
  borderRadius: '999px',
  background: 'var(--bg-elevated)',
  color: 'var(--text-secondary)',
  fontSize: '11px',
  fontWeight: 600,
};

const stageStackStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const panelStyle = {
  borderRadius: '22px',
  border: '1px solid var(--border-subtle)',
  background: 'var(--app-panel-bg)',
  boxShadow: 'var(--app-panel-shadow-soft)',
  padding: '22px',
};

const gateCardStyle = {
  borderRadius: '20px',
  border: '1px solid rgba(245,158,11,0.2)',
  background: 'rgba(245,158,11,0.08)',
  padding: '18px 20px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
};

const eyebrowStyle = {
  margin: '0 0 6px',
  color: 'var(--text-muted)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 700,
};

const sectionTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: '1.08rem',
  fontWeight: 700,
};

const entryShellStyle = {
  minHeight: 'min(78vh, 820px)',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  gap: '28px',
  padding: 'clamp(12px, 3vw, 24px) 0',
};

const entryHeaderStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  justifyContent: 'center',
};

const entryMicStageStyle = accent => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '22px',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'clamp(360px, 52vh, 520px)',
  borderRadius: '36px',
  overflow: 'hidden',
  background: `radial-gradient(circle at 50% 30%, ${accent}14, transparent 32%), radial-gradient(circle at 50% 62%, rgba(255,255,255,0.07), transparent 45%)`,
});

const entryOrbStyle = (color, x, size, opacity) => ({
  position: 'absolute',
  top: '50%',
  left: x,
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: '50%',
  background: color,
  opacity,
  filter: 'blur(48px)',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
});

const entryFooterStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const recordingHeroStyle = accent => ({
  borderRadius: '30px',
  border: '1px solid var(--border-subtle)',
  background: `radial-gradient(circle at top, ${accent}24, transparent 30%), linear-gradient(180deg, color-mix(in srgb, var(--bg-surface) 92%, transparent), color-mix(in srgb, var(--bg-elevated) 96%, transparent))`,
  boxShadow: 'var(--app-panel-shadow-soft)',
  padding: 'clamp(28px, 5vw, 48px)',
});

const entryEyebrowStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  width: 'fit-content',
  minHeight: '32px',
  padding: '0 12px',
  borderRadius: '999px',
  border: '1px solid color-mix(in srgb, var(--accent-primary) 16%, var(--border-default))',
  background: 'color-mix(in srgb, var(--accent-glow) 82%, var(--bg-elevated))',
  color: 'var(--accent-primary)',
  fontSize: '0.74rem',
  fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 700,
};

const entryTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-display)',
  fontSize: 'clamp(3rem, 8vw, 5rem)',
  lineHeight: 0.94,
};

const entryCopyStyle = {
  margin: 0,
  maxWidth: '440px',
  color: 'var(--text-secondary)',
  fontSize: '15px',
  lineHeight: 1.7,
};

const matterPillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '999px',
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-surface)',
  color: 'var(--text-secondary)',
  fontSize: '12px',
  fontWeight: 600,
};

const recordButtonStyle = ({ accent, disabled }) => ({
  position: 'relative',
  width: '196px',
  height: '196px',
  borderRadius: '50%',
  border: '1px solid color-mix(in srgb, white 24%, transparent)',
  background: `radial-gradient(circle at 30% 30%, color-mix(in srgb, ${accent} 72%, white 28%), color-mix(in srgb, ${accent} 94%, #f97316 6%))`,
  color: '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 28px 70px color-mix(in srgb, ${accent} 26%, transparent), 0 0 0 16px color-mix(in srgb, ${accent} 10%, transparent), inset 0 1px 0 rgba(255,255,255,0.28)`,
  opacity: disabled ? 0.58 : 1,
  transition: 'transform 160ms ease, box-shadow 160ms ease',
});

const recordingPreviewStyle = {
  padding: '14px 16px',
  borderRadius: '14px',
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  lineHeight: 1.8,
  whiteSpace: 'pre-wrap',
};

const signalCardStyle = {
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-surface)',
};

const transcriptEditorStyle = dirty => ({
  width: '100%',
  minHeight: '220px',
  borderRadius: '14px',
  border: `1px solid ${dirty ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
  background: 'var(--bg-elevated)',
  color: 'var(--text-primary)',
  padding: '14px 16px',
  resize: 'vertical',
  outline: 'none',
  lineHeight: 1.75,
  fontSize: '13px',
});

const transcriptToggleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  color: 'var(--text-muted)',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  padding: '4px 0',
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
};

const provenanceRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '11px 0',
  borderBottom: '1px solid var(--border-subtle)',
  color: 'var(--text-secondary)',
  fontSize: '13px',
};

const statusChipStyle = (background, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  borderRadius: '999px',
  background,
  color,
  fontSize: '11px',
  fontWeight: 700,
});

const dialogBackdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.55)',
  backdropFilter: 'blur(4px)',
};

const dialogCardStyle = {
  maxWidth: '420px',
  width: '90vw',
  borderRadius: '20px',
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-elevated)',
  padding: '28px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
};

const dialogIconStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '12px',
  background: 'rgba(245,158,11,0.14)',
  color: '#f59e0b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
